import type { CarVariant } from "@/services/cars/get-cars-models";
import type { Dispatch, SetStateAction } from "react";
import type { CarForm, StepFields } from "../interfaces/CreateCarForm.types";

export function validateStepFields(stepFields: StepFields, form: CarForm) {
  const requiredPositiveNumbers = ["purchase_price"];

  return (step: number) => {
    return stepFields[step].every((field) => {
      const value = form.values[field];

      if (typeof value === "string") {
        return value.trim().length > 0;
      }

      if (typeof value === "number" && requiredPositiveNumbers.includes(field)) {
        return value > 0;
      }

      return value !== undefined && value !== null;
    });
  };
}

export function nextStepHandler(
  stepFields: StepFields,
  activeStep: number,
  form: CarForm,
  setActiveStep: Dispatch<SetStateAction<number>>
) {
  return () => {
    const fields = stepFields[activeStep];

    if (fields.some((field) => form.validateField(field).hasError)) {
      return;
    }

    setActiveStep((current) => current + 1);
  };
}

function resetFields(
  form: CarForm,
  fields: (keyof typeof form.values)[],
  value?: string | null,
  NOVARIANT?: string
) {
  form.setFieldValue(fields[0], value || "");

  fields.slice(1).forEach((field) => {
    form.setFieldValue(field, "");
  });

  if (NOVARIANT) {
    form.setFieldValue("variant", NOVARIANT);
  }
}

export function handleMakeSelection(form: CarForm, NOVARIANT: string) {
  return (value?: string | null) => {
    resetFields(form, ["make", "family", "generation", "model"], value, NOVARIANT);
  };
}

export function updateCarFamilyAndResetFields(form: CarForm, NOVARIANT: string) {
  return (value?: string | null) => {
    resetFields(form, ["family", "generation", "model"], value, NOVARIANT);
  };
}

export function handleGenerationChangeFC(form: CarForm, NOVARIANT: string) {
  return (value?: string | null) => {
    resetFields(form, ["generation", "model"], value, NOVARIANT);
  };
}

export function handleModeChangeFC(
  form: CarForm,
  NOVARIANT: string,
  setMinProductionYear: Dispatch<SetStateAction<number>>,
  setMaxProductionYear: Dispatch<SetStateAction<number>>
) {
  return (value?: string | null) => {
    form.setFieldValue("model", value || "");
    form.setFieldValue("variant", NOVARIANT);

    setMinProductionYear(1900);
    setMaxProductionYear(new Date().getFullYear());
  };
}

export function updateProductionYearsByVariant(
  carVariants: CarVariant[] | undefined,
  NOVARIANT: string,
  setMinProductionYear: Dispatch<SetStateAction<number>>,
  setMaxProductionYear: Dispatch<SetStateAction<number>>,
  form: CarForm
) {
  return (variant: string) => {
    if (!carVariants) return;

    const currentYear = new Date().getFullYear();

    const selectedVariant =
      variant === NOVARIANT
        ? carVariants.find((item) => !item.variant)
        : carVariants.find((item) => item.variant === variant);

    const minYear = selectedVariant?.start_of_production || 1900;

    const maxYear =
      selectedVariant?.end_of_production === 9999
        ? currentYear
        : selectedVariant?.end_of_production || currentYear;

    setMinProductionYear(minYear);
    setMaxProductionYear(maxYear);

    if (form.isDirty()) {
      form.setFieldValue("production_year", maxYear);
    }
  };
}

export function selectDefaultVariantFC(
  NOVARIANT: string,
  form: CarForm,
  setMinMaxProdYearsByVariant: (variant: string) => void
) {
  return (carVariants: CarVariant[]) => {
    const sortedVariants = [...carVariants].sort((a, b) => {
      const aSplit = a.taxonomy_id.split(".");
      const bSplit = b.taxonomy_id.split(".");

      if (aSplit.length !== bSplit.length) {
        return aSplit.length - bSplit.length;
      }

      return parseInt(aSplit[aSplit.length - 1], 10) - parseInt(bSplit[bSplit.length - 1], 10);
    });

    const variantToSet = sortedVariants[0].variant ? sortedVariants[0].variant : NOVARIANT;

    form.setFieldValue("variant", variantToSet);
    setMinMaxProdYearsByVariant(variantToSet);
  };
}
